import React, { useState } from 'react';
import {
  Card, SectionTitle, NumberField, SelectField,
  ComparisonBlock, Rule, StatusBox, AlertBanner, ModalSimple, ReadonlyField,
} from './components';
import TablaDocumentos, { TablaLineasResumen } from './TablaDocumentos';
import { clp, pct } from '../../utils/cotizadorEngine';

export default function VistaEjecutivo({
  quote, calc, params, reglas, documentos,
  updateQuote, updateDocumento, onBuscarDeudor, deudorLoadingIdx,
  onAddDocumento, onRemoveDocumento, onGuardar, guardando,
}) {
  const [modalReglas, setModalReglas] = useState(false);
  const totales = calc.totales || {};

  return (
    <div className="space-y-6">
      <Card>
        <SectionTitle title="Cotización" subtitle="Tasa y descuentos a nivel operación." />
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4 xl:max-w-5xl">
          <SelectField label="Tipo cliente" value={quote.tipo_cliente} onChange={(v) => updateQuote('tipo_cliente', v)}
            options={[['Antiguo', 'Antiguo'], ['Nuevo', 'Nuevo'], ['Recurrente preferente', 'Recurrente preferente']]} />
          <SelectField label="Contrato vigente" value={quote.contrato_vigente} onChange={(v) => updateQuote('contrato_vigente', v)}
            options={[['Si', 'Sí'], ['No', 'No']]} />
          <SelectField label="Tipo contrato" value={quote.tipo_contrato} onChange={(v) => updateQuote('tipo_contrato', v)}
            options={[['No aplica', 'No aplica'], ['Digital', 'Digital'], ['Notarial', 'Notarial']]} />
          <NumberField label="Tasa operación real" value={quote.tasa_operacion_real} onChange={(v) => updateQuote('tasa_operacion_real', v)} suffix="% mens." step="0.001" />
          <NumberField label="Tasa objetivo base" value={quote.tasa_objetivo_base} onChange={(v) => updateQuote('tasa_objetivo_base', v)} suffix="% mens." step="0.001" />
          <NumberField label="Descuento real" value={quote.descuento_real} onChange={(v) => updateQuote('descuento_real', v)} prefix="$" />
          <NumberField label="Descuento campaña" value={quote.descuento_campana} onChange={(v) => updateQuote('descuento_campana', v)} prefix="$" />
          <NumberField label="Descuento ejecutivo" value={quote.descuento_ejecutivo} onChange={(v) => updateQuote('descuento_ejecutivo', v)} prefix="$" />
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(300px,380px)]">
        <Card>
          <SectionTitle title="Facturas" subtitle="Cada línea con su deudor, monto y plazo." />
          {calc.alertas?.map((a) => <AlertBanner key={a}>{a}</AlertBanner>)}
          <TablaDocumentos
            documentos={documentos}
            reglas={reglas}
            deudorLoadingIdx={deudorLoadingIdx}
            onUpdate={updateDocumento}
            onBuscarDeudor={onBuscarDeudor}
            onAdd={onAddDocumento}
            onRemove={onRemoveDocumento}
          />
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <ReadonlyField label="Deudores distintos" value={totales.cantidad_deudores ?? '—'} />
            <ReadonlyField label="Concentración máx." value={clp(totales.concentracion_deudor)} />
            <ReadonlyField label="Monto operación total" value={clp(totales.monto_operacion)} />
            <ReadonlyField label="Documentos" value={totales.cantidad_documentos ?? documentos.length} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setModalReglas(true)}
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Ver reglas aplicadas
            </button>
            <button
              type="button"
              onClick={onGuardar}
              disabled={guardando}
              className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {guardando ? 'Guardando...' : 'Guardar cotización'}
            </button>
          </div>
        </Card>

        <div className="space-y-4">
          <Card>
            <SectionTitle title="Operación real" subtitle="Totales agregados." />
            <ComparisonBlock rows={[
              ['Tasa operación', pct(quote.tasa_operacion_real)],
              ['Spread', pct(calc.real.spread), calc.real.cumple_spread ? 'green' : 'red'],
              ['Interés total', clp(calc.real.interes)],
              ['Comisión total', clp(calc.real.comision)],
              ['Gastos totales', clp(calc.real.gastos)],
              ['Ingreso neto', clp(calc.real.ingreso_neto)],
              ['Monto líquido', clp(calc.real.monto_liquido), 'green'],
              ['Margen neto', clp(calc.real.margen_neto), calc.real.margen_neto >= 0 ? 'green' : 'red'],
            ]} />
          </Card>
          <Card>
            <SectionTitle title="Política sugerida" subtitle="Precio recomendado." />
            <ComparisonBlock rows={[
              ['Clasificación resumen', calc.clasificacion.codigo || '—'],
              ['Comisión total', clp(calc.politica.comision)],
              ['Gastos totales', clp(calc.politica.gastos)],
              ['Monto líquido política', clp(calc.politica.monto_liquido), 'green'],
            ]} />
          </Card>
          <Card>
            <SectionTitle title="Pre-evaluación" />
            <div className="grid gap-2">
              {calc.pre_evaluacion.detalle.map((d) => (
                <Rule key={d.regla} ok={d.ok} label={`${d.regla}: ${d.criterio}`} />
              ))}
            </div>
            <StatusBox ok={calc.pre_evaluacion.cumple_todas} title={calc.pre_evaluacion.resultado} text="Reglas a nivel cotización y por factura." />
          </Card>
        </div>
      </div>

      <Card>
        <SectionTitle title="Detalle por factura" />
        <TablaLineasResumen lineas={calc.lineas} />
      </Card>

      <ModalSimple open={modalReglas} onClose={() => setModalReglas(false)} title="Reglas aplicadas en esta cotización">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-2">Categoría</th>
              <th className="p-2">Descripción</th>
              <th className="p-2">Fórmula</th>
              <th className="p-2">Monto</th>
              <th className="p-2">Aplica a</th>
            </tr>
          </thead>
          <tbody>
            {(calc.reglas_aplicadas || []).map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2 font-medium">{r.categoria}</td>
                <td className="p-2">{r.descripcion}</td>
                <td className="p-2 text-slate-600">{r.formula}</td>
                <td className="p-2">{r.monto != null ? clp(r.monto) : '—'}</td>
                <td className="p-2">{r.aplica_a}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ModalSimple>
    </div>
  );
}
